import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageAware } from 'src/app/general/language-aware';
import { TEditableComponent } from 'src/app/models/editable-component';
import { TEventListItemData } from 'src/app/models/event-list-item-data';
import { EditSystemService } from 'src/app/services/edit-system.service';
import { EventDescriptionService } from 'src/app/services/event-description.service';
import { companyRootRoute } from 'src/app/services/global.service';
import { MessageboxService } from 'src/app/services/messagebox.service';
import { DataTableGetter } from '../data-table/data-table.component';
type RowData = {
  column: string;
  item: TEventListItemData;
  row: number;
};
type EditState = {
  id: number;
  column: string;
};
type PreviousEditItemState = EditState & {
  hasChanged: boolean;
};

@Component({
  selector: 'app-event-descriptions-data-table',
  templateUrl: './event-descriptions-data-table.component.html',
  styleUrls: ['./event-descriptions-data-table.component.scss']
})
export class EventDescriptionsDataTableComponent extends LanguageAware implements OnInit, OnChanges, OnDestroy {
  @Input() public searchPhrase: string;
  @Input() public columns: string[] = ['default_name', 'active', 'cid_code', 'area_event', 'reaction_id', 'fake:delete'];
  @Output() public modifyColumns = new EventEmitter<void>();
  public loader: DataTableGetter<TEventListItemData>;
  public eventDescItemInEdit: EditState;
  public prevEventDescItemInEdit: PreviousEditItemState;
  public eventDescItemToSave: TEventListItemData;
  public newDropdownValue = {qualifier: undefined, reaction_id: undefined};
  public idsWithError = new Set<number>();
  public isLoading = false;
  public isSaved = false;
  private componentBeforeModification: TEventListItemData;
  private subscriptions: Subscription[] = [];
  private loadingStatusVisibilityDuration = 500;
  private savedStatusVisibilityDuration = 1000;
  private errorStatusVisibilityDuration = 1000;
  private hasMessageboxOpen = false;
  private savedStatusTimer;
  private loadingStatusTimer;
  constructor(
    cdRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    public edit: EditSystemService,
    private messagebox: MessageboxService,
    private router: Router,
    public eventDescription: EventDescriptionService,
  ) {
    super(cdRef);
    this.eventDescription.loadDescriptions();
    this.us.loadUserDataFromStorage();
    this.loader = this.eventDescription.getEventDescriptionsGetter();
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges) {
    if(changes.searchPhrase) {
      this.loader = this.eventDescription.getEventDescriptionsGetter(this.searchPhrase);
    }
  };

  ngOnDestroy(): void {
    if(this.subscriptions.length > 0) {
      this.subscriptions.forEach(sub => {
        sub.unsubscribe();
      });
    }
  }

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if(event.key === 'Escape' && this.eventDescItemInEdit) { this.onCancelButtonClick(); }
    if(event.key === 'Enter' && this.eventDescItemInEdit) { this.onSaveButtonClick(); }
  }

  public async onEditButtonClick(item: any, column: string): Promise<void> {
    if(this.eventDescItemInEdit) {
      this.prevEventDescItemInEdit = { ...this.eventDescItemInEdit, hasChanged: false};
      await this.exitEventDescriptionFieldInput();
      this.eventDescItemInEdit = { id: item.id, column };
    }
    this.eventDescItemInEdit = { id: item.id, column };
    this.edit.beginComponentEdit(TEditableComponent.EventDescription, item.id, item);
    this.componentBeforeModification = this.edit.getComponentBeforeModification(TEditableComponent.EventDescription);
    this.eventDescItemToSave = this.edit.getEditableComponent(TEditableComponent.EventDescription);
  }

  private async exitEventDescriptionFieldInput(): Promise<void> {
    if(JSON.stringify(this.eventDescItemToSave) !== JSON.stringify(this.componentBeforeModification)) {
      this.prevEventDescItemInEdit.hasChanged = true;
      await this.saveEventDescriptionItemChanges();
    }
    this.exitEditState();
  }

  private clearTimers() {
    if(this.savedStatusTimer) { clearTimeout(this.savedStatusTimer); };
    if(this.loadingStatusTimer) { clearTimeout(this.loadingStatusTimer); };
  }

  public async onToggleButtonClick(item: any, column: string, value: boolean) {
    if(this.eventDescItemInEdit) {
      this.prevEventDescItemInEdit = { ...this.eventDescItemInEdit, hasChanged: false };
      await this.exitEventDescriptionFieldInput();
    }
    this.eventDescItemInEdit = { id: item.id, column };
    this.edit.beginComponentEdit(TEditableComponent.EventDescription, item.id, item);
    this.eventDescItemToSave = this.edit.getEditableComponent(TEditableComponent.EventDescription);
    if(this.eventDescItemToSave.id && item.id) {
      if(column === 'active') {
        this.eventDescItemToSave.active = value;
      } else if (column === 'area_event') {
        this.eventDescItemToSave.area_event = value;
      }
      this.editEventDescriptionApiRequest();
    }
    this.edit.endComponentEdit(TEditableComponent.EventDescription);
  }

  public onSaveButtonClick() {
    if(this.isSaved) { return; }
    this.prevEventDescItemInEdit = { ...this.eventDescItemInEdit, hasChanged: false };
    this.exitEventDescriptionFieldInput();
  }

  public onCancelButtonClick() {
    Object.assign(this.eventDescItemToSave, this.componentBeforeModification);
    this.exitEditState();
  }

  private async saveEventDescriptionItemChanges(): Promise<void> {
    this.clearTimers();
    await this.editEventDescriptionApiRequest();
  }

  public exitEditState() {
    this.edit.endComponentEdit(TEditableComponent.EventDescription);
    this.eventDescItemInEdit = undefined;
  }

  private editEventDescriptionApiRequest(): Promise<void> {
    this.isLoading = true;
    this.isSaved = false;
    const itemId = this.eventDescItemToSave.id;
    return new Promise(resolve => {
      this.api.post('/edit/event-description', {
        id: this.eventDescItemToSave.id,
        code: this.eventDescItemToSave.event_code,
        name: this.eventDescItemToSave.default_name,
        reaction: this.eventDescItemToSave.reaction_id,
        active: this.eventDescItemToSave.active,
        qualifier: this.eventDescItemToSave.qualifier,
        area_event: this.eventDescItemToSave.area_event,
      }, true).subscribe(async result => {
        if(result.success) {
          if(this.componentBeforeModification && this.idsWithError.has(itemId)) { this.idsWithError.delete(itemId); };
          this.loadingStatusTimer = setTimeout(() => {
            this.isLoading = false;
            this.isSaved = true;
            this.savedStatusTimer = setTimeout(() => {
              this.isSaved = false;
              this.prevEventDescItemInEdit = undefined;
            }, this.savedStatusVisibilityDuration);
          }, this.loadingStatusVisibilityDuration);
        } else {
          if(!this.componentBeforeModification) {
            this.componentBeforeModification = this.edit.getComponentBeforeModification(TEditableComponent.EventDescription);
          }
          this.idsWithError.add(itemId);

          if(!this.hasMessageboxOpen) {
            this.hasMessageboxOpen = true;
            this.messagebox.open({
              buttons: this.messagebox.buttons.Ok,
              headerText: this.trans('general.titleError'),
              iconType: this.messagebox.iconType.Error,
              messageContent: result.error ? result.error : this.trans('general.errors.errorOccurred')
            },
            (messagebox) => {
              Object.assign(this.eventDescItemToSave, this.componentBeforeModification);
              const messageboxClose = messagebox.messageboxClose.subscribe(() => {
                this.hasMessageboxOpen = false;
                setTimeout(() => {
                  this.idsWithError.clear();
                }, this.errorStatusVisibilityDuration);
              });
              this.subscriptions.push(messageboxClose);
            });
          }
          this.isLoading = false;
          this.isSaved = false;
        }
        resolve();
      }, (error) => {
        this.messagebox.open({
          buttons: this.messagebox.buttons.Ok,
          headerText: this.trans('general.titleError'),
          iconType: this.messagebox.iconType.Error,
          messageContent: this.trans('general.errors.errorOccurred')
        });
      });
    });
  }

  public onRowClick(event: RowData): void {
    if(event.column === 'fake:delete') {
      this.onDeleteClick(event.item);
    }
  }

  public openEventForEditing(item) {
    this.edit.beginComponentEdit(TEditableComponent.EventDescription, item.id, item);
    this.router.navigate([companyRootRoute, 'settings', 'event-descriptions', 'edit', item.id]);
  }

  openReactionForEditing(id: number) {
    const reaction = this.us.currentUser.reactions.find(r => r.id === id);
    if(reaction) {
      this.edit.beginComponentEdit(TEditableComponent.Reaction, reaction.id, reaction);
      this.router.navigate([companyRootRoute, 'settings', 'reaction-types', 'edit', reaction.id]);
    }
  }

  public getReaction(id: number): { name: string; image_svg: string; image_png: string } {
    const reaction = this.us.currentUser.reactions.find(r => r.id === id);
    if(!reaction) {
      return {name: '', image_svg: '', image_png: ''};
    }
    const reactionName = reaction.name;
    const reactionImageSvg = reaction.image;
    const reactionImagePng = reaction.image_png;
    return {name: reactionName, image_svg: reactionImageSvg, image_png: reactionImagePng};
  }

  public getReactionsList(): Array<any> {
    const reactionsList = [];
    const reactions = this.us.currentUser.reactions;
    reactions.map(reaction => reactionsList.push({
      label: reaction.default_name ? this.trans(`reactionNames.reactions.${reaction.name}`): reaction.name,
      value: reaction.id
    }));
    return reactionsList;
  }

  private onDeleteClick(item): void {
    const messageText = this.sanitizer.bypassSecurityTrustHtml(
      `${this.trans('events.settings.deleteEventDescriptionInfo').replace(':event', item.default_name)}: ${this.trans('general.areYouSure')}`);
    this.messagebox.open({
      buttons: this.messagebox.buttons.YesNo,
      headerText: this.trans('general.confirm'),
      messageContent: messageText,
      iconType: this.messagebox.iconType.Warning
    }, (messagebox) => {
      const yesClickSubscription = messagebox.yesClicked.subscribe(() => {
        this.deletePostRequest(item);
      });
      this.subscriptions.push(yesClickSubscription);
    });
  }

  private deletePostRequest(item) {
    const that = this;
    const desc = this.eventDescription.eventDescriptions.find((ed) => ed.id === item.id);
    if (desc === undefined) {
      return;
    }
    this.eventDescription.eventDescriptions.splice(this.eventDescription.eventDescriptions.indexOf(desc), 1);
    this.api.post('/delete/event-description', { id: item.id }, true).subscribe(
      (result) => {
        if (!result.success) {
          const errorMessage = result.error ? result.error : '';
          that.messagebox.open({
            buttons: that.messagebox.buttons.Close,
            headerText: that.trans('general.titleError'),
            messageContent: `${that.trans('general.errors.errorOccurred')}${errorMessage !== '' ? `: ${errorMessage}` : '.'}`,
            iconType: that.messagebox.iconType.Error
          });
        } else {
          const itemInEdit = this.edit.getEditableComponent(TEditableComponent.EventDescription);
          if(itemInEdit && itemInEdit.id === item.id) {
            that.editSystemService.endComponentEdit(TEditableComponent.EventDescription);
            this.router.navigate([companyRootRoute, 'settings', 'event-descriptions']);
          }
        }
      },
      (error) => {
        that.messagebox.open({
          buttons: that.messagebox.buttons.Close,
          headerText: that.trans('general.titleError'),
          messageContent: that.trans('auth.errors.serverSideError'),
          iconType: that.messagebox.iconType.Error
        });
      }
    );
  }

}
